import React, { useEffect, useRef, useState } from "react";
import Header from "./Header";
import SubHeader from "./SubHeader";
import DataTable, { formatNum } from "./DataTable";
import TableLoader from "../TableLoader";
import Apiauth from "../../../API/Apiauth";
import MetalStoneChart from "./graph/MetalStoneChart";
import { toFormatPrice } from "../helpers/function";

function mapRows(apiData = []) {
  return apiData.map((row) => ({
    metal_name: row.metal_name,
    stone_name: row.stone_name,
    qty: row.qty,
    tagPrice: row.tag_price,
    sellingPrice: row.selling_price,
    qty_pct: row.qty_pct,
    amount_pct: row.amount_pct,
  }));
}

function mapTotals(apiTotals = {}, dataLength) {
  return {
    qty: apiTotals.qty,
    tagPrice: apiTotals.tag_price,
    sellingPrice: apiTotals.selling_price,
    // Keep totals row aligned with percentage columns in the table.
    qty_pct: dataLength > 0 ? 100 : 0,
    amount_pct: dataLength > 0 ? 100 : 0,
  };
}

function mapMetrics(summary = {}) {
  return [
    { label: "Metal", value: formatNum(summary.total_metals) },
    { label: "Qty", value: formatNum(summary.total_qty) },
    { label: "Total Sales Amount", value: toFormatPrice(Number(summary.total_sales || 0)) },
    { label: "Top Metal by Qty", value: summary.top_metal_by_qty ?? "-" },
  ];
}

const METAL_STONE_COLUMNS = [
  { key: "_index", label: "#", align: "center", width: 40 },
  { key: "metal_name", label: "Metal", align: "left", width: 180 },
  { key: "stone_name", label: "Stone", align: "left", width: 180 },
  { key: "qty", label: "Qty", align: "right", width: 120 },
  { key: "tagPrice", label: "Tag Price", align: "right", width: 170 },
  { key: "sellingPrice", label: "Selling Price", align: "right", width: 170 },
  {
    key: "qty_pct",
    label: "Qty% from over all sales",
    align: "right",
    width: 220,
  },
  {
    key: "amount_pct",
    label: "Amount% from over all sales",
    align: "right",
    width: 250,
  },
];

const DEFAULT_PAGE_SIZE = 10;
const CHART_MAX_LIMIT = 100000;

const MetalStone = () => {
  const [rows, setRows] = useState([]);
  const [chartRows, setChartRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [filter, setFilter] = useState({});
  const [loading, setLoading] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showCharts, setShowCharts] = useState(false);

  const fetchSalesByMetalStone = ({
    page: p = 1,
    limit: l = pageSize,
    ...rest
  } = {}) => {
    setLoading(true);

    Apiauth(
      { page: p, limit: l, ...rest },
      "Inventory/reports/salesByMetalStone",
    )
      .then((res) => {
        if (res?.code === 200) {
          setRows(mapRows(res.data));
          setTotals(mapTotals(res.totals, res.data.length));
          setMetrics(mapMetrics(res.summary));
          setFilter(res.filters ?? {});
          setTotalRecords(res?.pagination?.total ?? 0);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchChartReport = (filters = {}) => {
    setLoading(true);
    Apiauth(
      { page: 1, limit: CHART_MAX_LIMIT, ...filters },
      "Inventory/reports/salesByMetalStone",
    )
      .then((res) => {
        if (res?.code === 200) {
          setChartRows(mapRows(res.data));
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSalesByMetalStone({ page: 1, limit: DEFAULT_PAGE_SIZE });
  }, []);

  useEffect(() => {
    if (showCharts) {
      fetchChartReport(appliedFilters);
    }
  }, [showCharts, appliedFilters]);

  function handleFetchData({ page: newPage, limit: newLimit }) {
    if (newLimit !== pageSize) setPageSize(newLimit);
    setPage(newPage);
    fetchSalesByMetalStone({
      ...appliedFilters,
      page: newPage,
      limit: newLimit,
    });
  }

  function handleFilterApply(filterParams = {}) {
    setAppliedFilters(filterParams);
    setPage(1);
    fetchSalesByMetalStone({ ...filterParams, page: 1, limit: pageSize });
  }

  return (
    <>
      <Header title="Sales by Metal & Stone" metrics={metrics} />
      <SubHeader
        filters={filter}
        showCharts={showCharts}
        setShowCharts={setShowCharts}
        onFilterApply={handleFilterApply}
      />
      {loading ? (
        <TableLoader />
      ) : (
        showCharts?(<MetalStoneChart rows={chartRows}/>):
       ( <DataTable
          columns={METAL_STONE_COLUMNS}
          data={rows}
          totals={totals}
          showSearch={true}
          showDateRange={true}
          pageLimit={pageSize}
          totalRecords={totalRecords}
          currentPage={page}
          onPageChange={setPage}
          fetchData={handleFetchData}
        />)
      )}
    </>
  );
};

export default MetalStone;
